import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, Upload, Palette, Type, RotateCcw, Eye, Star, Download, ArrowUp, ArrowDown, Trash2, Plus, LayoutPanelTop, Smartphone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

import LogoImage from "@/components/LogoImage";

const FONT_OPTIONS = ["Outfit", "Inter", "Roboto", "Roboto Condensed", "Montserrat", "Titillium Web", "Orbitron", "Chakra Petch", "Racing Sans One"];

const DEFAULT_THEME = {
  primary_color: "#FF1E27",
  secondary_color: "#FFB703",
  bg_color: "#121214",
  card_color: "#2A2A32",
  border_color: "#3F3F4C",
  input_color: "#121214",
  heading_color: "#FFFFFF",
  text_color: "#E0E0E6",
  text_secondary_color: "#9CA3AF",
  link_color: "#FF1E27",
  cta_color: "#FF1E27",
  cta_hover_color: "#CC1118",
  btn_secondary_color: "#FFB703",
  badge_color: "#FFB703",
  status_racing_color: "#3B82F6",
  status_danger_color: "#EF4444",
  status_success_color: "#22C55E",
  status_warning_color: "#FFB703",
  login_bg_type: "color",
  login_bg_color: "#121214",
  login_gradient_from: "#121214",
  login_gradient_to: "#3F0713",
  login_gradient_angle: 135,
  login_bg_image: "",
  login_overlay_opacity: 55,
  login_card_opacity: 100,
  login_card_radius: 12,
  login_input_color: "#121214",
  login_welcome_message: "",
  show_sponsor_footer: true,
  show_trophy_wall: true,
  show_instagram_button: true,
  font_family: "Outfit",
  border_style: "rounded",
  card_style: "shadow",
  hero_gradient: false,
  gradient_from: "#121214",
  gradient_to: "#1A1A1E",
  header_sticky: true,
  nav_transparency: 10,
  footer_text: "",
  logo_height: 32,
  logo_width: 0,
  logo_margin: 8,
  hero_overlay: 0,
  pwa_short_name: "FantasyRace",
  pwa_name: "Fantasy Race — 69° Salita Monti Iblei",
  pwa_icon: "",
  pwa_splash_color: "#121214",
  pwa_theme_color: "#121214",
};

const toDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export async function deleteAssetIfInternal(url) {
  if (url && url.includes("/api/assets/branding/")) {
    const name = url.split("/").pop();
    try {
      await api.delete(`/admin/upload-asset/${name}`);
    } catch {
      /* asset gia' rimosso */
    }
  }
}

function ImageField({ label, value, onChange, testid }) {
  const pickFile = async (file) => {
    if (!file) return;
    if (file.size > 10000000) {
      toast.error("File troppo grande (max 10 MB)");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-asset", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Immagine caricata sullo storage permanente");
    } catch (err) {
      onChange(await toDataUrl(file));
      toast.warning("Storage non raggiungibile: salvata come copia incorporata");
    }
  };
  const clear = async () => {
    await deleteAssetIfInternal(value);
    onChange("");
    toast.success("Immagine rimossa");
  };
  return (
    <div className="space-y-2">
      <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
        {label}
      </label>
      <div className="flex items-center gap-3">
        <div
          className="w-14 h-14 rounded-lg border border-dashed border-[#3F3F4C] bg-[#121214] flex items-center justify-center overflow-hidden shrink-0"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            pickFile(e.dataTransfer.files?.[0]);
          }}
          title="Trascina qui un'immagine"
        >
          {value ? (
            <LogoImage src={value} alt={label} hideOnError className="max-w-full max-h-full object-contain" />
          ) : (
            <Upload className="w-4 h-4 text-gray-600" />
          )}
        </div>
        <div className="flex-1 space-y-1.5 min-w-0">
          <div className="flex items-center gap-1.5">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              data-testid={`${testid}-file`}
              onChange={(e) => pickFile(e.target.files?.[0])}
              className="block w-full text-[11px] text-gray-400 file:mr-2 file:rounded-lg file:border-0 file:bg-[#FF1E27] file:px-3 file:py-1.5 file:text-[10px] file:font-bold file:text-white file:uppercase file:tracking-widest hover:file:bg-[#CC1118] file:cursor-pointer"
            />
            {value && (
              <button
                type="button"
                data-testid={`${testid}-delete`}
                onClick={clear}
                title="Elimina immagine"
                className="shrink-0 p-2 rounded-lg border border-[#3F3F4C] text-gray-500 hover:text-[#FF1E27] hover:border-[#FF1E27] transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <input
            data-testid={`${testid}-url`}
            className="w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-2.5 py-1.5 text-[11px] text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] font-time"
            placeholder="...oppure incolla URL immagine"
            value={value?.startsWith("data:") ? "" : value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid, alpha = false }) {
  const hex = (value || "#000000").slice(0, 7);
  const a = value && value.length === 9 ? Math.round((parseInt(value.slice(7, 9), 16) / 255) * 100) : 100;
  const compose = (h, alphaPct) => {
    if (!alpha || alphaPct >= 100) return h;
    const ai = Math.round((alphaPct / 100) * 255).toString(16).padStart(2, "0");
    return `${h}${ai}`;
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-[#3F3F4C] bg-[#121214] px-3 py-2.5">
      <span className="font-body-sport text-[11px] font-bold tracking-widest text-gray-400 uppercase">
        {label}
      </span>
      <div className="flex items-center gap-2">
        {alpha && (
          <input
            type="range"
            min={0}
            max={100}
            value={a}
            data-testid={`${testid}-alpha`}
            title={`Opacità ${a}%`}
            onChange={(e) => onChange(compose(hex, parseInt(e.target.value)))}
            className="w-16 accent-[#FF1E27]"
          />
        )}
        <span className="font-time text-[11px] text-gray-500">{value}</span>
        <input
          type="color"
          data-testid={testid}
          value={hex}
          onChange={(e) => onChange(compose(e.target.value, a))}
          className="w-10 h-8 rounded cursor-pointer bg-transparent border border-[#3F3F4C]"
        />
      </div>
    </div>
  );
}

function SliderField({ label, value, onChange, min, max, testid, unit = "px" }) {
  return (
    <div className="space-y-1.5">
      <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
        {label}: {value}{unit}
      </label>
      <input
        type="range"
        min={min}
        max={max}
        data-testid={testid}
        className="w-full accent-[#FF1E27]"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
      />
    </div>
  );
}

function LivePreview({ form }) {
  const radius = form.border_style === "sharp" ? "0px" : form.border_style === "pill" ? "24px" : "12px";
  const cardShadow =
    form.card_style === "shadow"
      ? "0 10px 30px rgba(0,0,0,.55)"
      : form.card_style === "neon"
      ? `0 0 22px ${form.primary_color}40`
      : "none";
  const cardBg = form.card_style === "glass" ? `${form.card_color}90` : form.card_color;
  const cardBorder =
    form.card_style === "neon"
      ? `1px solid ${form.primary_color}`
      : form.card_style === "glass"
      ? "1px solid rgba(255,255,255,0.14)"
      : `1px solid ${form.border_color}`;

  return (
    <div
      data-testid="theme-live-preview"
      className="rounded-xl border border-[#3F3F4C] p-5 space-y-4"
      style={{
        background: form.hero_gradient ? `linear-gradient(160deg, ${form.gradient_from}, ${form.gradient_to})` : form.bg_color,
        fontFamily: `'${form.font_family}', sans-serif`,
      }}
    >
      <p className="font-body-sport text-[10px] font-bold tracking-[0.3em] uppercase flex items-center gap-2" style={{ color: form.text_secondary_color }}>
        <Eye className="w-3.5 h-3.5" /> Anteprima Live
      </p>
      <h3 className="font-racing font-black text-lg uppercase" style={{ color: form.heading_color }}>
        Titolo <span style={{ color: form.primary_color }}>Esempio</span>
      </h3>
      <div
        className="p-4 space-y-3"
        style={{ background: cardBg, borderRadius: radius, boxShadow: cardShadow, border: cardBorder, backdropFilter: form.card_style === "glass" ? "blur(14px)" : "none" }}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="font-bold text-sm" style={{ color: form.text_color }}>Pilota Esempio</p>
          <span className="text-[10px] font-bold px-2 py-0.5" style={{ background: form.badge_color, color: "#000", borderRadius: radius }}>
            CAPTAIN 1.5X
          </span>
        </div>
        <p className="text-xs" style={{ color: form.text_secondary_color }}>
          Osella FA30 — Gruppo E2SC — Testo secondario con <span style={{ color: form.link_color, textDecoration: "underline" }}>link di esempio</span>
        </p>
        <input
          readOnly
          placeholder="Input di esempio"
          className="w-full px-3 py-2 text-xs"
          style={{ background: form.input_color, border: `1px solid ${form.border_color}`, borderRadius: radius, color: form.text_color }}
        />
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            data-testid="preview-cta"
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-white transition-colors"
            style={{ background: form.cta_color, borderRadius: radius }}
            onMouseEnter={(e) => (e.currentTarget.style.background = form.cta_hover_color)}
            onMouseLeave={(e) => (e.currentTarget.style.background = form.cta_color)}
          >
            Aggiungi
          </button>
          <button
            type="button"
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest border"
            style={{ borderColor: form.btn_secondary_color, color: form.btn_secondary_color, borderRadius: radius }}
          >
            <Star className="w-3 h-3 inline" /> Capitano
          </button>
        </div>
      </div>
    </div>
  );
}

const THEME_PRESETS = [
  {
    id: "rosso-corsa", nome: "Rosso Corsa", desc: "Il look originale della 69° Salita",
    colors: ["#FF1E27", "#FFB703", "#121214"],
    values: { primary_color: "#FF1E27", secondary_color: "#FFB703", bg_color: "#121214", card_color: "#2A2A32", border_color: "#3F3F4C", input_color: "#121214", heading_color: "#FFFFFF", text_color: "#E0E0E6", text_secondary_color: "#9CA3AF", link_color: "#FF1E27", cta_color: "#FF1E27", cta_hover_color: "#CC1118", btn_secondary_color: "#FFB703", badge_color: "#FFB703", status_racing_color: "#3B82F6", status_danger_color: "#EF4444", status_success_color: "#22C55E", status_warning_color: "#FFB703", font_family: "Outfit" },
  },
  {
    id: "notte-iblea", nome: "Notte Iblea", desc: "Blu notte e cyan, luci della sera",
    colors: ["#0EA5E9", "#22D3EE", "#0B1220"],
    values: { primary_color: "#38BDF8", secondary_color: "#22D3EE", bg_color: "#0B1220", card_color: "#111C2E", border_color: "#24344D", input_color: "#0B1220", heading_color: "#F0F9FF", text_color: "#DBEAFE", text_secondary_color: "#7FA3C7", link_color: "#38BDF8", cta_color: "#0EA5E9", cta_hover_color: "#0369A1", btn_secondary_color: "#22D3EE", badge_color: "#FACC15", status_racing_color: "#38BDF8", status_danger_color: "#FB7185", status_success_color: "#34D399", status_warning_color: "#FACC15", font_family: "Inter" },
  },
  {
    id: "oro-campione", nome: "Oro Campione", desc: "Nero e oro da podio",
    colors: ["#D4AF37", "#F5D76E", "#0A0A0A"],
    values: { primary_color: "#D4AF37", secondary_color: "#F5D76E", bg_color: "#0A0A0A", card_color: "#171412", border_color: "#3A3226", input_color: "#0A0A0A", heading_color: "#F5D76E", text_color: "#EDE3C8", text_secondary_color: "#A89A7C", link_color: "#D4AF37", cta_color: "#D4AF37", cta_hover_color: "#B8962E", btn_secondary_color: "#F5D76E", badge_color: "#F5D76E", status_racing_color: "#7EC8E3", status_danger_color: "#E5484D", status_success_color: "#46A758", status_warning_color: "#F5D76E", font_family: "Montserrat" },
  },
  {
    id: "gulf-racing", nome: "Gulf Racing", desc: "Azzurro e arancio, leggenda motorsport",
    colors: ["#F58020", "#7EC8E3", "#0E1A26"],
    values: { primary_color: "#F58020", secondary_color: "#7EC8E3", bg_color: "#0E1A26", card_color: "#132433", border_color: "#2A4356", input_color: "#0E1A26", heading_color: "#FFFFFF", text_color: "#DCE9F2", text_secondary_color: "#8FA9BC", link_color: "#7EC8E3", cta_color: "#F58020", cta_hover_color: "#D96C12", btn_secondary_color: "#7EC8E3", badge_color: "#F58020", status_racing_color: "#7EC8E3", status_danger_color: "#E5484D", status_success_color: "#30A46C", status_warning_color: "#F5A623", font_family: "Titillium Web" },
  },
];

export default function BrandingTab() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const [form, setForm] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (settings && !form) {
      setForm({
        ...DEFAULT_THEME,
        logo_url: "", mobile_logo_url: "", footer_logo_url: "", favicon_url: "",
        race_logo_url: "", organizer_logo_url: "", sponsor_logo_url: "", sponsor_logos: [],
        hero_image: "", hero_image_mobile: "", credits_bg_image: "", maintenance_bg_image: "",
        ...settings.branding,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  if (!form) return null;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await api.put("/admin/content", { branding: form });
      toast.success("Modifiche grafiche pubblicate in tempo reale");
      qc.invalidateQueries({ queryKey: ["settings-public"] });
    } catch (e) {
      toast.error(errMsg(e, "Salvataggio fallito"));
    }
  };

  const resetTheme = async () => {
    const media = {};
    ["logo_url", "logo_height", "logo_width", "logo_margin", "mobile_logo_url", "footer_logo_url", "favicon_url", "race_logo_url", "organizer_logo_url", "sponsor_logo_url", "sponsor_logos", "hero_image", "hero_image_mobile", "hero_overlay", "credits_bg_image", "maintenance_bg_image", "pwa_icon"].forEach((k) => (media[k] = form[k]));
    const next = { ...form, ...DEFAULT_THEME, ...media };
    setForm(next);
    try {
      await api.put("/admin/content", { branding: next });
      toast.success("Tema ripristinato ai valori default (loghi mantenuti)");
      qc.invalidateQueries({ queryKey: ["settings-public"] });
    } catch (e) {
      toast.error(errMsg(e, "Ripristino fallito"));
    }
  };

  const exportTheme = () => {
    const blob = new Blob([JSON.stringify(form, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fantasyrace-theme.json";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Configurazione tema esportata");
  };

  const moveSponsor = (i, dir) => {
    const arr = [...(form.sponsor_logos || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set("sponsor_logos")(arr);
  };

  const selectCls =
    "w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";
  const h3Cls = "font-body-sport font-bold text-white text-sm uppercase tracking-widest flex items-center gap-2";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-5">
        <h3 className={h3Cls}>
          <Upload className="w-4 h-4 text-[#FF1E27]" /> Assets & Loghi
        </h3>
        <ImageField label="Logo Header — consigliato 250x60 px (PNG trasparente)" value={form.logo_url} onChange={set("logo_url")} testid="branding-logo" />
        <div className="grid grid-cols-3 gap-3 pl-1">
          <SliderField label="Altezza" value={form.logo_height} onChange={set("logo_height")} min={16} max={80} testid="branding-logo-height" />
          <SliderField label="Larghezza (0=auto)" value={form.logo_width} onChange={set("logo_width")} min={0} max={200} testid="branding-logo-width" />
          <SliderField label="Margine" value={form.logo_margin} onChange={set("logo_margin")} min={0} max={32} testid="branding-logo-margin" />
        </div>
        <ImageField label="Logo Mobile (compatto)" value={form.mobile_logo_url} onChange={set("mobile_logo_url")} testid="branding-mobile-logo" />
        <ImageField label="Favicon / Icona App" value={form.favicon_url} onChange={set("favicon_url")} testid="branding-favicon" />
        <ImageField label="Logo Organizzatore (Dinamika APS)" value={form.organizer_logo_url} onChange={set("organizer_logo_url")} testid="branding-organizer" />
        <ImageField label="Logo Competizione (69° Monti Iblei)" value={form.race_logo_url} onChange={set("race_logo_url")} testid="branding-race-logo" />
        <ImageField label="Logo Footer — consigliato 200x50 px" value={form.footer_logo_url} onChange={set("footer_logo_url")} testid="branding-footer-logo" />

        <div className="space-y-2 pt-2 border-t border-[#3F3F4C]">
          <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
            Griglia Loghi Sponsor & Partner ({(form.sponsor_logos || []).length})
          </label>
          {(form.sponsor_logos || []).map((u, i) => (
            <div
              key={u || `empty-${i}`}
              draggable
              data-testid={`sponsor-row-${i}`}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIdx === null || dragIdx === i) return;
                const arr = [...form.sponsor_logos];
                const [moved] = arr.splice(dragIdx, 1);
                arr.splice(i, 0, moved);
                set("sponsor_logos")(arr);
                setDragIdx(null);
              }}
              className="flex items-center gap-2 cursor-move rounded-lg"
              title="Trascina per riordinare"
            >
              <div className="w-10 h-10 rounded border border-[#3F3F4C] bg-[#121214] flex items-center justify-center overflow-hidden shrink-0">
                {u ? <LogoImage src={u} alt="" hideOnError className="max-w-full max-h-full object-contain" /> : <Upload className="w-3 h-3 text-gray-600" />}
              </div>
              <input
                data-testid={`sponsor-url-${i}`}
                className="flex-1 min-w-0 rounded-lg bg-[#121214] border border-[#3F3F4C] px-2 py-1.5 text-[11px] text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] font-time"
                placeholder="URL logo sponsor"
                value={u?.startsWith("data:") ? "" : u}
                onChange={(e) => {
                  const arr = [...form.sponsor_logos];
                  arr[i] = e.target.value;
                  set("sponsor_logos")(arr);
                }}
              />
              <label className="shrink-0 cursor-pointer rounded-lg border border-[#3F3F4C] px-2 py-1.5 text-[10px] font-bold text-gray-400 hover:text-white hover:border-[#FF1E27] transition-colors">
                FILE
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  className="hidden"
                  data-testid={`sponsor-file-${i}`}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 10000000) { toast.error("Logo troppo grande (max 10MB)"); return; }
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      const { data } = await api.post("/admin/upload-asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      const arr = [...form.sponsor_logos];
                      arr[i] = data.url;
                      set("sponsor_logos")(arr);
                      toast.success("Logo caricato sullo storage permanente");
                    } catch {
                      const arr = [...form.sponsor_logos];
                      arr[i] = await toDataUrl(f);
                      set("sponsor_logos")(arr);
                      toast.warning("Storage non raggiungibile: salvato come copia incorporata");
                    }
                  }}
                />
              </label>
              <button data-testid={`sponsor-up-${i}`} onClick={() => moveSponsor(i, -1)} className="p-1.5 text-gray-500 hover:text-white transition-colors"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button data-testid={`sponsor-down-${i}`} onClick={() => moveSponsor(i, 1)} className="p-1.5 text-gray-500 hover:text-white transition-colors"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button
                data-testid={`sponsor-remove-${i}`}
                onClick={async () => {
                  await deleteAssetIfInternal(form.sponsor_logos[i]);
                  set("sponsor_logos")(form.sponsor_logos.filter((_, j) => j !== i));
                }}
                className="p-1.5 text-gray-500 hover:text-[#FF1E27] transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button
            data-testid="sponsor-add"
            onClick={() => set("sponsor_logos")([...(form.sponsor_logos || []), ""])}
            className="flex items-center gap-1.5 rounded-lg border border-dashed border-[#3F3F4C] px-3 py-2 text-[11px] font-bold text-gray-400 hover:text-[#FFB703] hover:border-[#FFB703] transition-colors font-body-sport uppercase tracking-widest"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi Logo
          </button>
        </div>

        <div className="pt-2 border-t border-[#3F3F4C] space-y-5">
          <ImageField label="Sfondo Dashboard Desktop — consigliato 1920x1080 px (WebP/JPG)" value={form.hero_image} onChange={set("hero_image")} testid="branding-hero" />
          <ImageField label="Sfondo Dashboard Mobile — consigliato 1080x1920 px (o 800x1200)" value={form.hero_image_mobile} onChange={set("hero_image_mobile")} testid="branding-hero-mobile" />
          <SliderField label="Overlay scuro hero" value={form.hero_overlay} onChange={set("hero_overlay")} min={0} max={90} testid="branding-hero-overlay" unit="%" />
          <ImageField label="Sfondo Modal Crediti" value={form.credits_bg_image} onChange={set("credits_bg_image")} testid="branding-credits-bg" />
          <ImageField label="Sfondo Schermata Manutenzione" value={form.maintenance_bg_image} onChange={set("maintenance_bg_image")} testid="branding-maintenance-bg" />
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3">
          <h3 className={h3Cls}>
            <Palette className="w-4 h-4 text-[#FFB703]" /> Theme Engine — Colori
          </h3>
          <p className="font-body-sport text-[9px] font-bold tracking-widest text-gray-600 uppercase">Palette Principale</p>
          <ColorField label="Primario / Accent" value={form.primary_color} onChange={set("primary_color")} testid="branding-primary-color" />
          <ColorField label="Secondario (Oro)" value={form.secondary_color} onChange={set("secondary_color")} testid="branding-secondary-color" />
          <ColorField label="Sfondo Generale" value={form.bg_color} onChange={set("bg_color")} testid="branding-bg-color" />
          <p className="font-body-sport text-[9px] font-bold tracking-widest text-gray-600 uppercase pt-2">Componenti UI</p>
          <ColorField label="Sfondo Card / Container" value={form.card_color} onChange={set("card_color")} testid="branding-card-color" />
          <ColorField label="Bordi Card" value={form.border_color} onChange={set("border_color")} testid="branding-border-color" />
          <ColorField label="Sfondo Input / Form" value={form.input_color} onChange={set("input_color")} testid="branding-input-color" />
          <p className="font-body-sport text-[9px] font-bold tracking-widest text-gray-600 uppercase pt-2">Testi & Tipografia</p>
          <ColorField label="Testo Titoli (H1-H6)" value={form.heading_color} onChange={set("heading_color")} testid="branding-heading-color" />
          <ColorField label="Testo Corpo" value={form.text_color} onChange={set("text_color")} testid="branding-text-color" />
          <ColorField label="Testo Secondario / Muted" value={form.text_secondary_color} onChange={set("text_secondary_color")} testid="branding-text2-color" />
          <ColorField label="Colore Link" value={form.link_color} onChange={set("link_color")} testid="branding-link-color" />
          <p className="font-body-sport text-[9px] font-bold tracking-widest text-gray-600 uppercase pt-2">Pulsanti & Stati</p>
          <ColorField label="Pulsante Primario (CTA)" value={form.cta_color} onChange={set("cta_color")} testid="branding-cta-color" />
          <ColorField label="CTA Hover / Pressione" value={form.cta_hover_color} onChange={set("cta_hover_color")} testid="branding-cta-hover" />
          <ColorField label="Pulsante Secondario" value={form.btn_secondary_color} onChange={set("btn_secondary_color")} testid="branding-btn2-color" />
          <ColorField label="Badge di Stato / Ruoli" value={form.badge_color} onChange={set("badge_color")} testid="branding-badge-color" />
          <div className="flex items-center justify-between rounded-lg border border-[#3F3F4C] bg-[#121214] px-3 py-2.5">
            <span className="font-body-sport text-[11px] font-bold tracking-widest text-gray-400 uppercase">Gradiente sfondo</span>
            <Switch data-testid="branding-gradient-toggle" checked={form.hero_gradient} onCheckedChange={set("hero_gradient")} />
          </div>
          {form.hero_gradient && (
            <div className="grid grid-cols-2 gap-2">
              <ColorField label="Da" value={form.gradient_from} onChange={set("gradient_from")} testid="branding-gradient-from" />
              <ColorField label="A" value={form.gradient_to} onChange={set("gradient_to")} testid="branding-gradient-to" />
            </div>
          )}
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3">
          <h3 className={h3Cls}>
            <Type className="w-4 h-4 text-[#FFB703]" /> Tipografia, Bordi & Effetti
          </h3>
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">Font di sistema</label>
            <select data-testid="branding-font-select" className={selectCls} value={form.font_family} onChange={(e) => set("font_family")(e.target.value)}>
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">Stile bordura</label>
            <select data-testid="branding-border-select" className={selectCls} value={form.border_style} onChange={(e) => set("border_style")(e.target.value)}>
              <option value="sharp">Racing / Sharp (0px)</option>
              <option value="rounded">Modern / Smooth (8-12px)</option>
              <option value="pill">Soft / Pill (20px+)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">Effetto visivo card</label>
            <select data-testid="branding-card-select" className={selectCls} value={form.card_style} onChange={(e) => set("card_style")(e.target.value)}>
              <option value="minimal">Minimal</option>
              <option value="shadow">Con Ombra (Box Shadow)</option>
              <option value="glass">Lucido / Glassmorphism</option>
              <option value="neon">Bordo Neon / Glow</option>
            </select>
          </div>
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-4">
          <h3 className={h3Cls}>
            <LayoutPanelTop className="w-4 h-4 text-[#FF1E27]" /> Header, Navigation & Footer
          </h3>
          <div className="flex items-center justify-between rounded-lg border border-[#3F3F4C] bg-[#121214] px-3 py-2.5">
            <span className="font-body-sport text-[11px] font-bold tracking-widest text-gray-400 uppercase">Header Fisso (Sticky Nav)</span>
            <Switch data-testid="branding-header-sticky" checked={form.header_sticky} onCheckedChange={set("header_sticky")} />
          </div>
          <SliderField label="Trasparenza barra di navigazione" value={form.nav_transparency} onChange={set("nav_transparency")} min={0} max={100} testid="branding-nav-transparency" unit="%" />
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">Testo Footer (copyright / note legali)</label>
            <input
              data-testid="branding-footer-text"
              className={selectCls}
              placeholder="FantasyRace — Dinamika APS — Chiaramonte Gulfi (RG)"
              value={form.footer_text}
              onChange={(e) => set("footer_text")(e.target.value)}
            />
          </div>
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-4" data-testid="pwa-setup-section">
          <h3 className={h3Cls}>
            <Smartphone className="w-4 h-4 text-[#FF1E27]" /> Configurazione App Mobile (PWA)
          </h3>
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
              Nome App Schermata Home
            </label>
            <input
              data-testid="pwa-short-name-input"
              className={selectCls}
              placeholder="FantasyRace"
              value={form.pwa_short_name}
              onChange={(e) => set("pwa_short_name")(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
              Nome Esteso App
            </label>
            <input
              data-testid="pwa-name-input"
              className={selectCls}
              placeholder="Fantasy Race — 69° Salita Monti Iblei"
              value={form.pwa_name}
              onChange={(e) => set("pwa_name")(e.target.value)}
            />
          </div>
          <ImageField label="Icona Web App / PWA — consigliato 512x512 px (PNG quadrato senza trasparenze, per la schermata Home)" value={form.pwa_icon} onChange={set("pwa_icon")} testid="pwa-icon" />
          <ColorField label="Sfondo Splash Screen" value={form.pwa_splash_color} onChange={set("pwa_splash_color")} testid="pwa-splash-color" />
          <ColorField label="Barra di Stato (Theme Color)" value={form.pwa_theme_color} onChange={set("pwa_theme_color")} testid="pwa-theme-color" />
          <p className="text-[10px] text-gray-500">
            Il manifest.json viene generato automaticamente con questi dati (display standalone, icone any+maskable).
          </p>
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3" data-testid="theme-presets-section">
          <h3 className={h3Cls}>
            <Palette className="w-4 h-4 text-[#FFB703]" /> Preset Grafici One-Click
          </h3>
          <p className="text-[11px] text-gray-500 font-body-sport">
            Applica un look completo in un click, poi premi "Pubblica Modifiche Grafiche" per renderlo definitivo.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {THEME_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`theme-preset-${p.id}`}
                onClick={() => {
                  setForm((f) => ({ ...f, ...p.values }));
                  toast.success(`Preset "${p.nome}" applicato in anteprima — Pubblica per renderlo definitivo`);
                }}
                className="rounded-lg border border-[#3F3F4C] hover:border-[#FF1E27] bg-[#121214] p-3 text-left transition-colors"
              >
                <div className="flex gap-1.5 mb-2">
                  {p.colors.map((c) => (
                    <span key={c} className="w-5 h-5 rounded-full border border-white/20" style={{ background: c }} />
                  ))}
                </div>
                <p className="font-body-sport font-bold text-white text-xs uppercase tracking-wider">{p.nome}</p>
                <p className="text-[10px] text-gray-500">{p.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-4" data-testid="login-customizer-section">
          <h3 className={h3Cls}>
            <LayoutPanelTop className="w-4 h-4 text-[#FF1E27]" /> Customizer Login & Auth
          </h3>
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">Tipo di sfondo pagina Login</label>
            <select data-testid="login-bg-type" className={selectCls} value={form.login_bg_type} onChange={(e) => set("login_bg_type")(e.target.value)}>
              <option value="color">Colore solido</option>
              <option value="gradient">Gradiente dinamico</option>
              <option value="image">Immagine caricata</option>
            </select>
          </div>
          {form.login_bg_type === "color" && (
            <ColorField label="Colore sfondo" value={form.login_bg_color} onChange={set("login_bg_color")} testid="login-bg-color" alpha />
          )}
          {form.login_bg_type === "gradient" && (
            <>
              <ColorField label="Gradiente — Da" value={form.login_gradient_from} onChange={set("login_gradient_from")} testid="login-gradient-from" />
              <ColorField label="Gradiente — A" value={form.login_gradient_to} onChange={set("login_gradient_to")} testid="login-gradient-to" />
              <SliderField label="Angolo gradiente" value={form.login_gradient_angle} onChange={set("login_gradient_angle")} min={0} max={360} testid="login-gradient-angle" unit="°" />
            </>
          )}
          {form.login_bg_type === "image" && (
            <>
              <ImageField label="Immagine di sfondo (JPG/PNG/WEBP)" value={form.login_bg_image} onChange={set("login_bg_image")} testid="login-bg-image" />
              <SliderField label="Velo scuro (leggibilità)" value={form.login_overlay_opacity} onChange={set("login_overlay_opacity")} min={0} max={90} testid="login-overlay-opacity" unit="%" />
            </>
          )}
          <SliderField label="Opacità card di accesso" value={form.login_card_opacity} onChange={set("login_card_opacity")} min={40} max={100} testid="login-card-opacity" unit="%" />
          <SliderField label="Arrotondamento card" value={form.login_card_radius} onChange={set("login_card_radius")} min={0} max={32} testid="login-card-radius" />
          <ColorField label="Sfondo campi di input" value={form.login_input_color} onChange={set("login_input_color")} testid="login-input-color" />
          <div className="space-y-1.5">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
              Messaggio di benvenuto (supporta **grassetto** e a-capo)
            </label>
            <textarea
              data-testid="login-welcome-message"
              rows={3}
              className={selectCls}
              placeholder="Es. **Benvenuto alla 69° Salita!** Schiera la tua squadra e sfida gli amici."
              value={form.login_welcome_message}
              onChange={(e) => set("login_welcome_message")(e.target.value)}
            />
          </div>
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3" data-testid="status-colors-section">
          <h3 className={h3Cls}>
            <Palette className="w-4 h-4 text-[#3B82F6]" /> Colori di Stato (Live Timing & Badge)
          </h3>
          <ColorField label="In Gara / Info" value={form.status_racing_color} onChange={set("status_racing_color")} testid="status-racing-color" />
          <ColorField label="DNF / Errore / Penalità" value={form.status_danger_color} onChange={set("status_danger_color")} testid="status-danger-color" />
          <ColorField label="Successo / Ufficiale" value={form.status_success_color} onChange={set("status_success_color")} testid="status-success-color" />
          <ColorField label="Warning / In attesa" value={form.status_warning_color} onChange={set("status_warning_color")} testid="status-warning-color" />
        </div>

        <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3" data-testid="modules-toggle-section">
          <h3 className={h3Cls}>
            <LayoutPanelTop className="w-4 h-4 text-[#FFB703]" /> Moduli Visibili (ON/OFF)
          </h3>
          {[
            ["show_sponsor_footer", "Banner & Loghi Sponsor nel Footer", "mod-sponsor-footer"],
            ["show_trophy_wall", "Bacheca Trofei nel Profilo", "mod-trophy-wall"],
            ["show_instagram_button", "Pulsante Instagram (Ora sei Dinamiko/a!)", "mod-instagram"],
          ].map(([k, label, tid]) => (
            <div key={k} className="flex items-center justify-between rounded-lg border border-[#3F3F4C] bg-[#121214] px-3 py-2.5">
              <span className="font-body-sport text-[11px] font-bold tracking-widest text-gray-400 uppercase">{label}</span>
              <Switch data-testid={tid} checked={form[k] !== false} onCheckedChange={(v) => set(k)(v)} />
            </div>
          ))}
        </div>

        <LivePreview form={form} />
      </div>

      <div className="lg:col-span-2 flex flex-wrap gap-3">
        <button
          data-testid="branding-save-button"
          onClick={save}
          className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-5 py-2.5 min-h-[48px] font-racing font-bold text-xs tracking-widest text-white uppercase"
        >
          <Save className="w-4 h-4" /> Pubblica Modifiche Grafiche
        </button>
        <button
          data-testid="branding-export-button"
          onClick={exportTheme}
          className="flex items-center gap-2 rounded-lg border border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10 transition-colors px-5 py-2.5 min-h-[48px] font-body-sport font-bold text-xs tracking-widest uppercase"
        >
          <Download className="w-4 h-4" /> Esporta Tema (JSON)
        </button>
        <button
          data-testid="branding-reset-button"
          onClick={resetTheme}
          className="flex items-center gap-2 rounded-lg border border-[#3F3F4C] text-gray-300 hover:border-[#FFB703] hover:text-[#FFB703] transition-colors px-5 py-2.5 min-h-[48px] font-body-sport font-bold text-xs tracking-widest uppercase"
        >
          <RotateCcw className="w-4 h-4" /> Ripristina Tema Default
        </button>
      </div>
    </div>
  );
}
